// src/stores/usuarioStore.ts

import { defineStore } from "pinia";
import axios from "axios";
import KeycloakService from "@/core/services/KeycloakService";
import type { PerfilDados } from "@/core/types/usuario";

export const useUsuarioStore = defineStore("usuario", {
  state: () => ({
    id: null as number | null,
    nome: null as string | null,
    usuario: null as string | null,
    email: null as string | null,
    situacao: null as string | null,
    cadastradoEm: null as string | null,
    perfilDados: null as PerfilDados | null,
    perfilSelecionado: null as string | null,
    carregando: false,
    erro: null as string | null,
    dadosCarregados: false,
    ultimaAtualizacao: null as string | null, // Para controle de cache
  }),

  actions: {
    async carregarDadosUsuario(forcarRecarga = false) {
      // Se já tem dados carregados e não é para forçar recarga, retorna
      if (this.dadosCarregados && !forcarRecarga) {
        return;
      }

      // Se tem dados persistidos e são recentes (menos de 5 minutos), usa eles
      if (!forcarRecarga && this.dadosCarregados && this.ultimaAtualizacao) {
        const agora = new Date().getTime();
        const ultimaAtualizacao = new Date(this.ultimaAtualizacao).getTime();
        const cincoMinutos = 5 * 60 * 1000; // 5 minutos em ms

        if (agora - ultimaAtualizacao < cincoMinutos) {
          return;
        }
      }

      try {
        this.carregando = true;
        this.erro = null;

        const token = process.env.VUE_APP_JWT_TOKEN;
        const keycloakToken = KeycloakService.getInstance().token;
        const id = KeycloakService.getInstance().subject;
        const apiUrl = `${process.env.VUE_APP_ADMIN_API}/usuario/${id}`;

        const result = await axios.get(apiUrl, {
          headers: {
            "x-autenticacao": `Bearer ${keycloakToken}`,
            Authorization: `Bearer ${token}`,
            "X-Apikey": `Bearer ${token}`,
          },
        });

        const dados = result.data?.dados;

        if (dados) {
          this.id = dados.id;
          this.nome = dados.nome;
          this.usuario = dados.usuario;
          this.email = dados.email;
          this.situacao = dados.situacao;
          this.cadastradoEm = dados.cadastradoEm;

          if (dados.atributos) {
            if (
              dados.atributos.perfil_dados &&
              dados.atributos.perfil_dados.length > 0
            ) {
              try {
                this.perfilDados = JSON.parse(dados.atributos.perfil_dados[0]);
              } catch (e) {
                this.perfilDados = null;
              }
            }

            this.perfilSelecionado = dados.atributos.perfil_selecionado || null;
          }

          this.dadosCarregados = true;
          this.ultimaAtualizacao = new Date().toISOString();
        }
      } catch (error: any) {
        this.erro = error.message;

        // Se deu erro mas tem dados em cache, mantém eles
        if (!this.dadosCarregados) {
          this.perfilDados = null;
        }
      } finally {
        this.carregando = false;
      }
    },

    // Método para forçar recarga dos dados
    async recarregarDados() {
      await this.carregarDadosUsuario(true);
    },

    limparDados() {
      this.id = null;
      this.nome = null;
      this.usuario = null;
      this.email = null;
      this.situacao = null;
      this.cadastradoEm = null;
      this.perfilDados = null;
      this.perfilSelecionado = null;
      this.dadosCarregados = false;
      this.ultimaAtualizacao = null;
      this.erro = null;
    },

    // Método para verificar se os dados estão válidos
    verificarValidadeDados() {
      if (!this.ultimaAtualizacao) return false;

      const agora = new Date().getTime();
      const ultimaAtualizacao = new Date(this.ultimaAtualizacao).getTime();
      const umDia = 24 * 60 * 60 * 1000; // 1 dia em ms

      return agora - ultimaAtualizacao < umDia;
    },
  },

  getters: {
    nomeUsuario: (state) => state.nome,
    emailUsuario: (state) => state.email,
    dadosCompletos: (state) => ({
      id: state.id,
      nome: state.nome,
      usuario: state.usuario,
      email: state.email,
      situacao: state.situacao,
      cadastradoEm: state.cadastradoEm,
      perfilDados: state.perfilDados,
      perfilSelecionado: state.perfilSelecionado,
    }),
    estaCarregado: (state) => state.dadosCarregados && !state.carregando,
    temDadosValidos: (state) => {
      if (!state.ultimaAtualizacao || !state.dadosCarregados) return false;

      const agora = new Date().getTime();
      const ultimaAtualizacao = new Date(state.ultimaAtualizacao).getTime();
      const umDia = 24 * 60 * 60 * 1000;

      return agora - ultimaAtualizacao < umDia;
    },
  },

  persist: {
    key: "usuario-store",
    storage: localStorage,
    // Persiste tudo exceto o estado de carregamento e erro
    paths: [
      "id",
      "nome",
      "usuario",
      "email",
      "situacao",
      "cadastradoEm",
      "perfilDados",
      "perfilSelecionado",
      "dadosCarregados",
      "ultimaAtualizacao",
    ],
  },
});
